var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { Icon, Text } from '..';
import { useTheme } from '../../theme';
import { useMultipleMemoClasses, useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        margin: 'var(--RUI-margin)',
        display: 'flex',
        flexDirection: 'column',
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Table.base)); },
    table: {
        font: 'var(--RUI-font-body1)',
        borderCollapse: 'collapse',
        borderLeft: '0px',
        borderRight: '0px',
        width: '100%',
        borderRadius: 'calc(var(--RUI-border-radius) / 2 )',
        overflow: 'hidden',
    },
    tableOverrides: function (theme) { return (__assign({}, theme.overrides.Table.table)); },
    primary: {
        '--RUI-table-main': 'var(--RUI-color-primary)',
        '--RUI-table-light': 'var(--RUI-color-primary-light)',
        '--RUI-table-lighter': 'var(--RUI-color-primary-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUI-table-main': 'var(--RUI-color-secondary)',
        '--RUI-table-light': 'var(--RUI-color-secondary-light)',
        '--RUI-table-lighter': 'var(--RUI-color-secondary-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUI-table-main': 'var(--RUI-color-neutral)',
        '--RUI-table-light': 'var(--RUI-color-neutral-light)',
        '--RUI-table-lighter': 'var(--RUI-color-neutral-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUI-table-main': 'var(--RUI-color-error)',
        '--RUI-table-light': 'var(--RUI-color-error-light)',
        '--RUI-table-lighter': 'var(--RUI-color-error-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-error-contrast)',
    },
    warning: {
        '--RUI-table-main': 'var(--RUI-color-warning)',
        '--RUI-table-light': 'var(--RUI-color-warning-light)',
        '--RUI-table-lighter': 'var(--RUI-color-warning-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-warning-contrast)',
    },
    success: {
        '--RUI-table-main': 'var(--RUI-color-success)',
        '--RUI-table-light': 'var(--RUI-color-success-light)',
        '--RUI-table-lighter': 'var(--RUI-color-success-lighter)',
        '--RUI-table-contrast': 'var(--RUI-color-success-contrast)',
    },
    tableData: {
        border: 'var(--RUI-border-width) solid var(--RUI-table-contrast)',
        borderLeft: '0px',
        borderRight: '0px',
        textAlign: 'left',
        padding: 'calc(0.5 * var(--RUI-padding)) calc(0.7 * var(--RUI-padding))',
    },
    tableDataOverrides: function (theme) { return (__assign({}, theme.overrides.Table.tableData)); },
    tableHeader: {
        backgroundColor: 'var(--RUI-table-main)',
        color: 'var(--RUI-table-contrast)',
    },
    tableHeaderOverrides: function (theme) { return (__assign({}, theme.overrides.Table.tableHeader)); },
    tableRow: {
        color: 'var(--RUI-color-paper-contrast)',
        backgroundColor: 'var(--RUI-color-paper-dark)',
    },
    tableRowOverrides: function (theme) { return (__assign({}, theme.overrides.Table.tableRow)); },
    fullColor: {
        color: 'var(--RUI-table-contrast)',
        backgroundColor: 'var(--RUI-table-light)',
        '&$striped': {
            '&:nth-child(odd)': {
                backgroundColor: 'var(--RUI-table-lighter)',
            },
        },
    },
    borderless: {
        border: '0px',
    },
    striped: {
        '&:nth-child(odd)': {
            backgroundColor: 'var(--RUI-color-paper-darker)',
        },
    },
    paginationContainer: {
        marginTop: 'var(--RUI-margin)',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    paginationContainerOverrides: function (theme) { return (__assign({}, theme.overrides.Table.paginationContainer)); },
    paginationButton: {
        backgroundColor: 'transparent',
        padding: 0,
        border: 0,
        cursor: 'pointer',
    },
    paginationButtonOverrides: function (theme) { return (__assign({}, theme.overrides.Table.paginationButton)); },
    paginationText: {
        margin: '0 calc(0.5 * var(--RUI-margin))',
    },
    paginationTextOverrides: function (theme) { return (__assign({}, theme.overrides.Table.paginationText)); },
    paginationOutline: {
        border: 'var(--RUI-border-width) solid var(--RUI-color-neutral-darker)',
        borderRadius: 'calc(var(--RUI-border-radius) / 2 )',
    },
}, { name: 'RUITable' });
var Table = function (_a) {
    var className = _a.className, style = _a.style, columns = _a.columns, dataSource = _a.dataSource, numberOfRows = _a.numberOfRows, pagination = _a.pagination, paginationOutline = _a.paginationOutline, striped = _a.striped, color = _a.color, fullColor = _a.fullColor, borderless = _a.borderless, onClickBodyRow = _a.onClickBodyRow, props = __rest(_a, ["className", "style", "columns", "dataSource", "numberOfRows", "pagination", "paginationOutline", "striped", "color", "fullColor", "borderless", "onClickBodyRow"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var _b = useState(1), page = _b[0], setPage = _b[1];
    var _c = useState(0), totalRows = _c[0], setTotalRows = _c[1];
    var _d = useState([]), dataRows = _d[0], setDataRows = _d[1];
    var numberOfPages = useMemo(function () {
        setTotalRows(0);
        setPage(1);
        if (dataSource) {
            if (dataSource.length > numberOfRows) {
                return ((dataSource.length % numberOfRows) + numberOfRows) % numberOfRows === 0
                    ? dataSource.length / numberOfRows
                    : Math.trunc(dataSource.length / numberOfRows + 1);
            }
            else {
                return 1;
            }
        }
    }, [dataSource]);
    var _e = useMultipleMemoClasses([
        [
            styles.paginationContainer,
            theme.overrides.Table.paginationContainer && styles.paginationContainerOverrides,
            'RUITable-pagination-container',
        ],
        [
            styles.paginationText,
            theme.overrides.Table.paginationText && styles.paginationTextOverrides,
            'RUITable-pagination-text',
        ],
    ], [theme]), paginationContainerClasses = _e[0], paginationTextClasses = _e[1];
    var baseClasses = useMemoClasses([styles.base, styles[color], theme.overrides.Table.base && styles.baseOverrides, 'RUITable-base'], [theme, color]);
    var tableHeaderClasses = useMemoClasses([
        styles.tableHeader,
        styles[color],
        borderless && styles.borderless,
        theme.overrides.Table.tableHeader && styles.tableHeaderOverrides,
        'RUITable-header',
    ], [theme, color, borderless]);
    var tableClasses = useMemoClasses([styles.table, styles[color], theme.overrides.Table.table && styles.tableOverrides, 'RUITable-table', className], [theme, className, color]);
    var tableRowClasses = useMemoClasses([
        styles.tableRow,
        fullColor && styles.fullColor,
        striped && styles.striped,
        theme.overrides.Table.tableRow && styles.tableRowOverrides,
        'RUITable-body',
    ], [theme, striped, fullColor]);
    var tableDataClasses = useMemoClasses([
        styles.tableData,
        borderless && styles.borderless,
        theme.overrides.Table.tableData && styles.tableDataOverrides,
        'RUITable-Content',
    ], [theme, borderless]);
    var paginationButtonClasses = useMemoClasses([
        styles.paginationButton,
        paginationOutline && styles.paginationOutline,
        theme.overrides.Table.paginationButton && styles.paginationButtonOverrides,
        'RUITable-pagination-button',
    ], [theme, paginationOutline]);
    var headers = useMemo(function () {
        if (columns && columns.length > 0) {
            return (React.createElement("tr", { className: tableHeaderClasses }, columns.map(function (column) { return (React.createElement("th", { key: column.key, className: tableDataClasses }, column.title)); })));
        }
    }, [columns]);
    useEffect(function () {
        var updatedRows = [];
        if (dataSource && dataSource.length > 0) {
            dataSource.forEach(function (data, index) {
                if (((totalRows === 0 || totalRows <= index) && totalRows + numberOfRows > index) || !pagination) {
                    updatedRows.push(React.createElement("tr", { key: data.key, className: tableRowClasses, onClick: function () { return onClickBodyRow && onClickBodyRow(data); } }, columns &&
                        columns.map(function (column) { return (React.createElement("td", { className: tableDataClasses, key: column.key }, data[column.key])); })));
                }
            });
        }
        setDataRows(updatedRows);
    }, [totalRows, dataSource, numberOfRows]);
    return (React.createElement("div", { className: baseClasses },
        React.createElement("table", __assign({}, props, { style: style, className: tableClasses }),
            React.createElement("thead", null, headers),
            React.createElement("tbody", null, dataRows.map(function (row) { return row; }))),
        pagination && columns && dataSource && numberOfPages > 1 && (React.createElement("div", { className: paginationContainerClasses },
            React.createElement("button", { disabled: !(totalRows - numberOfRows >= 0), className: paginationButtonClasses, onClick: function () {
                    setTotalRows(totalRows - numberOfRows);
                    setPage(page - 1);
                } },
                React.createElement(Icon, null, "navigate_before")),
            React.createElement(Text, { variant: "caption", className: paginationTextClasses }, page >= numberOfPages ? numberOfPages : "".concat(page, " of ").concat(numberOfPages)),
            React.createElement("button", { disabled: !(page < numberOfPages), className: paginationButtonClasses, onClick: function () {
                    setTotalRows(totalRows + numberOfRows);
                    setPage(page + 1);
                } },
                React.createElement(Icon, null, "navigate_next"))))));
};
Table.defaultProps = {
    columns: null,
    dataSource: null,
    fullColor: false,
    numberOfRows: 10,
    pagination: true,
    paginationOutline: false,
    striped: false,
    borderless: false,
    color: 'neutral',
};
export { Table as default, Table };
